import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import TableScrollContainer from '../components/TableScrollContainer';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import './CrmClientes.css';

function CrmClientes() {
  const [resumo, setResumo] = useState(null);
  const [clientesRetorno, setClientesRetorno] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [filtroNome, setFiltroNome] = useState('');

  const fetchData = async () => {
    try {
      setErro('');
      const [resumoResponse, retornosResponse] = await Promise.all([
        api.get('/clientes/crm/resumo'),
        api.get('/clientes/crm/retornos?limit=200'),
      ]);
      setResumo(resumoResponse.data);
      setClientesRetorno(retornosResponse.data);
    } catch (error) {
      setErro(error.response?.data?.error || 'Nao foi possivel carregar o CRM.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const clientesFiltrados = useMemo(() => {
    const query = filtroNome.trim().toLowerCase();
    if (!query) return clientesRetorno;
    return clientesRetorno.filter((item) => item.nome.toLowerCase().includes(query));
  }, [clientesRetorno, filtroNome]);

  const openDetalhe = async (clienteId) => {
    try {
      const response = await api.get(`/clientes/${clienteId}/crm-metrics`);
      setDetalhe(response.data);
    } catch (error) {
      setErro(error.response?.data?.error || 'Nao foi possivel abrir detalhes do cliente.');
    }
  };

  if (loading) {
    return <p className="loading">Carregando CRM...</p>;
  }

  return (
    <div className="crm-clientes-page">
      <h1>CRM de Clientes</h1>
      {erro && <div className="crm-error">{erro}</div>}

      <div className="crm-summary-grid">
        <div className="crm-summary-card">
          <h3>Qtd de clientes</h3>
          <p>{resumo?.total_clientes || 0}</p>
        </div>
        <div className="crm-summary-card">
          <h3>Clientes ativos (90d)</h3>
          <p>{resumo?.clientes_ativos_90d || 0}</p>
        </div>
        <div className="crm-summary-card">
          <h3>Quando retornar</h3>
          <p>{resumo?.clientes_para_retorno || 0} pendentes</p>
        </div>
        <div className="crm-summary-card">
          <h3>Taxa de clientes ativos</h3>
          <p>{Number(resumo?.share_clientes_ativos_90d || 0).toFixed(1)}%</p>
        </div>
      </div>

      <div className="card">
        <div className="crm-toolbar">
          <h2>Retorno e frequencia</h2>
          <input
            type="text"
            value={filtroNome}
            onChange={(event) => setFiltroNome(event.target.value)}
            placeholder="Buscar cliente"
          />
        </div>
        <p className="crm-toolbar-helper">
          Share da tabela = percentual da receita total que cada cliente representa no período dos lançamentos.
        </p>

        <TableScrollContainer rows={clientesFiltrados.length} threshold={10} maxHeight="min(72vh, 760px)">
          <table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Qtd de idas</th>
                <th>Ultima visita</th>
                <th>Retorno previsto</th>
                <th>Frequencia</th>
                <th>Share da receita</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {clientesFiltrados.map((item) => (
                <tr key={item.cliente_id}>
                  <td>{item.nome}</td>
                  <td>{item.total_visitas}</td>
                  <td>{formatDate(item.ultima_visita)}</td>
                  <td>{formatDate(item.retorno_previsto)}</td>
                  <td>{item.frequencia_label} ({item.frequencia_dias}d)</td>
                  <td>{Number(item.share_receita_percentual || 0).toFixed(1)}%</td>
                  <td>
                    <button className="crm-detail-btn" type="button" onClick={() => openDetalhe(item.cliente_id)}>
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScrollContainer>
      </div>

      <Modal isOpen={!!detalhe} onClose={() => setDetalhe(null)} title="Detalhes do cliente (CRM)">
        {detalhe && (
          <div className="crm-detalhe-modal">
            <h3>{detalhe.cliente.nome}</h3>
            <p>
              Frequencia: <strong>{detalhe.indicadores?.frequencia_label || '-'} ({detalhe.indicadores?.frequencia_dias || 0} dias)</strong>
            </p>
            <p>
              Share de receita: <strong>{Number(detalhe.indicadores?.share_receita_percentual || 0).toFixed(1)}%</strong>
            </p>
            <p>
              Total gasto: <strong>{formatCurrency(detalhe.indicadores?.total_gasto || 0)}</strong>
            </p>
            <p>
              Última visita: <strong>{formatDate(detalhe.indicadores?.ultima_visita)}</strong>
            </p>
            <p>
              Retorno previsto: <strong>{formatDate(detalhe.indicadores?.retorno_previsto)}</strong>
            </p>

            <h4>Ultimas visitas</h4>
            <ul className="crm-history-list">
              {detalhe.historico.map((item) => (
                <li key={item.id}>
                  {formatDate(item.data_competencia)} - {item.servico_nome} - {formatCurrency(item.valor)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CrmClientes;
